import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    SetStateAction
} from 'react'
import Slider from 'react-slick'
import styles from './GroupedProduct.module.scss'
import { ChevronFatRight, Cross } from '@components/icons'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { SidebarGroupedProduct } from '@corratech/pylot-ui'
import { useTranslation } from 'next-i18next/pages'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { Button } from '@corratech/pylot-ui'
import type { MediaGalleryInterface } from '@pylot-data/pylotschema'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import getStockValidation from '@pylot-data/api/operations/get-stock-validation'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { RichContent } from '@corratech/pylot-rich-content'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { graphqlFetch } from '@pylot-data/graphqlFetch'

import { ProductImage } from '@pylot-data/pylotschema'
import cn from 'classnames'

interface Product {
    id: number
    name: string
    url_key: string
    image?: ProductImage
    price_range?: any
    sku: string
    media_gallery?: MediaGalleryInterface[]
    description?: { html?: string }
    short_description?: { html?: string }
    medidas_y_materialidad?: string
    ppum?: number | string
    is_set?: number
}

export interface GroupedProductItem {
    id: number
    name: string
    url_key: string
    image?: ProductImage
    price_range?: any
    sku: string
    media_gallery?: MediaGalleryInterface[]
    description?: { html?: string }
    short_description?: { html?: string }
    medidas_y_materialidad?: string
    ppum?: number | string
    is_set?: number
}

interface GroupedProductProps {
    // puede venir con nulos
    products?: Array<Product | null | undefined>
    onSelectionChange?: (selected: GroupedProductItem[]) => void
    /** Descripción del producto agrupado (short_description.html) */
    descriptionHtml?: string
    /** Botón de wishlist ya armado (para el set completo) */
    wishlist?: React.ReactNode
    /** price_range del producto agrupado (para mostrar el precio base del set) */
    groupedPriceRange?: any
}

function GroupedProduct({
    products,
    onSelectionChange,
    descriptionHtml,
    wishlist,
    groupedPriceRange
}: GroupedProductProps) {
    const sliderRef = useRef<Slider | null>(null)
    const [currentSlide, setCurrentSlide] = useState(0)
    const { t } = useTranslation('pdp')
    const infoMessage = t(
        'pdp||Review your cart, we adjusted the available units for one of your products'
    )
    const [isSidebarOpen, setSidebarOpen] = useState(false)
    const [selectedProduct, setSelectedProduct] =
        useState<GroupedProductItem | null>(null)
    const [isBulkAdding, setIsBulkAdding] = useState(false)
    const [sidebarMedia, setSidebarMedia] = useState<MediaGalleryInterface[]>(
        []
    )
    const [isSidebarLoading, setIsSidebarLoading] = useState(false)

    const { openSidebar, openCartToast, openToast, closeSidebar } = useUI()
    const {
        setQuantity,
        quantity,
        variant,
        selectedOptionUIDs,
        isButtonDisabled,
        buttonLabel: buttonLabelWithoutAdding
    } = useProductUI(selectedProduct)

    const isMobile = isMobileScreen()
    const { addToCart, isAdding } = useAddToCart()
    const slidesToShow = 3
    const TIMEOUT_AFTER_OPENING_SIDEBAR = 50
    const buttonLabel = isAdding
        ? t('Adding...')
        : buttonLabelWithoutAdding === 'Add to cart'
          ? t('Add to cart')
          : buttonLabelWithoutAdding
    // Sanitiza la lista
    const safeProducts: Product[] = useMemo(
        () =>
            (products ?? []).filter(
                (p): p is Product => !!p && typeof p.id === 'number' && !!p.sku
            ),
        [products]
    )

    // Mapa de selección por sku (tildado por defecto)
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({})

    // mapa de cantidad por sku
    const [qtyMap, setQtyMap] = useState<Record<string, number>>({})

    const fetchProductBySku = async (sku: string) => {
        setIsSidebarLoading(true)
        try {
            const response = await graphqlFetch<
                { sku: string },
                {
                    products: {
                        items: Array<
                            Product & {
                                media_gallery?: MediaGalleryInterface[]
                                short_description?: { html?: string }
                                medidas_y_materialidad?: string
                            }
                        >
                    }
                }
            >({
                query: /* GraphQL */ `
                    query GroupedItemBySku($sku: String!) {
                        products(filter: { sku: { eq: $sku } }) {
                            items {
                                id
                                sku
                                name
                                short_description {
                                    html
                                }
                                medidas_y_materialidad
                                media_gallery {
                                    url
                                    label
                                    position
                                }
                            }
                        }
                    }
                `,
                variables: { sku }
            })

            const item = response?.data?.products?.items?.[0]
            if (item) {
                setSelectedProduct((prev) =>
                    prev ? { ...prev, ...item } : item
                )
                setSidebarMedia(item.media_gallery ?? [])
            } else {
                setSidebarMedia([])
            }
        } catch (e) {
            console.error('Error fetching grouped item by SKU', e)
            setSidebarMedia([])
        } finally {
            setIsSidebarLoading(false)
        }
    }

    useEffect(() => {
        if (!safeProducts.length) return

        if (Object.keys(checkedMap).length > 0) return

        const initialChecked: Record<string, boolean> = {}
        const initialQty: Record<string, number> = {}

        for (const p of safeProducts) {
            initialChecked[p.sku] = true
            initialQty[p.sku] = 1
        }

        setCheckedMap(initialChecked)
        setQtyMap(initialQty)
    }, [safeProducts])

    const selectedProducts = useMemo(
        () => safeProducts.filter((p) => checkedMap[p.sku]),
        [safeProducts, checkedMap]
    )

    // Aviso opcional al padre
    useEffect(() => {
        if (!onSelectionChange) return
        onSelectionChange(selectedProducts)
    }, [onSelectionChange, selectedProducts])

    const checkedCount = useMemo(
        () => Object.values(checkedMap).filter(Boolean).length,
        [checkedMap]
    )

    // Cantidad total (sumando qty de cada sku tildado)
    const dynamicGroupQty = useMemo(
        () =>
            selectedProducts.reduce((acc, p) => {
                const q = qtyMap[p.sku] ?? 1
                return acc + q
            }, 0),
        [selectedProducts, qtyMap]
    )

    // Precio total dinámico del set
    const dynamicGroupPrice = useMemo(
        () =>
            selectedProducts.reduce((acc, p) => {
                const value =
                    p?.price_range?.minimum_price?.final_price?.value ??
                    p?.price_range?.maximum_price?.final_price?.value ??
                    0
                const num =
                    typeof value === 'number'
                        ? value
                        : Number.parseFloat(value ?? 0)
                const q = qtyMap[p.sku] ?? 1
                return acc + num * q
            }, 0),
        [selectedProducts, qtyMap]
    )

    const groupedSetFinalPrice = useMemo(() => {
        if (safeProducts.length === 0) return undefined

        const total = safeProducts.reduce((acc, p) => {
            const value =
                p?.price_range?.minimum_price?.final_price?.value ??
                p?.price_range?.maximum_price?.final_price?.value ??
                0
            const num =
                typeof value === 'number'
                    ? value
                    : Number.parseFloat(value ?? 0)
            const q = qtyMap[p.sku] ?? 1
            return acc + num * q
        }, 0)

        if (total > 0) return total

        if (!groupedPriceRange) return undefined
        const possibleFinalPrices = [
            groupedPriceRange?.final_price,
            groupedPriceRange?.minimum_price?.final_price,
            groupedPriceRange?.maximum_price?.final_price
        ]
        for (const price of possibleFinalPrices) {
            if (!price) continue
            const amount =
                typeof price === 'number'
                    ? price
                    : (price?.amount ?? price?.value ?? Number(price))
            const parsed =
                typeof amount === 'number'
                    ? amount
                    : Number.parseFloat(amount ?? 0)
            if (!Number.isNaN(parsed)) {
                return parsed
            }
        }
        return undefined
    }, [groupedPriceRange, qtyMap, safeProducts])

    if (safeProducts.length === 0) return null

    const shouldUseSlider = safeProducts.length > slidesToShow
    const settings = {
        dots: false,
        infinite: false,
        speed: 300,
        slidesToShow,
        slidesToScroll: 1,
        arrows: false,
        beforeChange: (_: number, next: number) => setCurrentSlide(next)
    }

    const isFirstSlide = currentSlide === 0
    const isLastSlide = currentSlide >= safeProducts.length - slidesToShow

    const handlePrev = () => {
        if (!isFirstSlide) sliderRef.current?.slickPrev()
    }
    const handleNext = () => {
        if (!isLastSlide) sliderRef.current?.slickNext()
    }

    const handleOpenSidebar = (product: Product) => {
        setSelectedProduct(product)
        setQuantity(qtyMap[product.sku] ?? 1)
        setSidebarOpen(true)

        fetchProductBySku(product.sku)
    }
    const handleCloseSidebar = () => {
        setSidebarOpen(false)
        setSelectedProduct(null)
    }

    const toggleChecked = (sku: string) => {
        setCheckedMap((m) => ({ ...m, [sku]: !m[sku] }))
    }

    const canShowPrice = (p: Product) => !!p?.price_range

    // Agregar el producto abierto en el sidebar
    const addProductHandler = async () => {
        if (!selectedProduct || !variant) return

        const currentQty = qtyMap[selectedProduct.sku] ?? quantity

        const stockValidationSku = [
            { sku: selectedProduct.sku, quantity: currentQty.toString() }
        ]

        const { getValidationStock: beforePlaceOrderStockValidation } =
            await getStockValidation({ input: { items: stockValidationSku } })

        const stock = Number(beforePlaceOrderStockValidation?.[0]?.stock ?? 0)
        const validQuantity = currentQty > stock
        const addToCartOptions = selectedOptionUIDs

        const response = await addToCart([
            {
                sku: variant.sku,
                uid: variant.uid,
                ...(variant.sku !== selectedProduct.sku && {
                    parent_sku: selectedProduct.sku
                }),
                selected_options: addToCartOptions,
                quantity: validQuantity ? stock : currentQty
            }
        ])

        const errorMessage =
            response?.errors?.[0]?.message ??
            response?.user_errors?.[0]?.message ??
            null
        const successMessage = t('{{name}} was added to cart', {
            name: selectedProduct.name
        })

        if (!isMobile) {
            openSidebar()
            setTimeout(() => {
                closeSidebar()
            }, 4000)
        }

        if (response?.data && !errorMessage) {
            const { cart } = response.data
            const addedProduct = {
                ...selectedProduct,
                sku: variant.sku,
                uid: variant.uid
            }
            cartUpdateEvent('add', addedProduct as any, cart)
        }

        handleCloseSidebar()

        setTimeout(
            () =>
                isMobile
                    ? openToast(
                          validQuantity
                              ? infoMessage
                              : errorMessage
                                ? errorMessage
                                : successMessage,
                          validQuantity
                              ? ToastType.Warning
                              : errorMessage
                                ? ToastType.Warning
                                : ToastType.Success,
                          true
                      )
                    : openCartToast(
                          validQuantity
                              ? infoMessage
                              : errorMessage
                                ? errorMessage
                                : successMessage,
                          validQuantity
                              ? ToastType.Warning
                              : errorMessage
                                ? ToastType.Warning
                                : ToastType.Success
                      ),
            TIMEOUT_AFTER_OPENING_SIDEBAR
        )

        setQuantity(1)
    }

    // Agregar TODOS los tildados
    const addSelectedToCart = async () => {
        if (selectedProducts.length === 0) {
            openToast(
                t('No hay productos seleccionados'),
                ToastType.Warning,
                true
            )
            return
        }

        setIsBulkAdding(true)

        try {
            const stockValidationItems = selectedProducts.map((p) => ({
                sku: p.sku,
                quantity: String(qtyMap[p.sku] ?? 1)
            }))

            const { getValidationStock } = await getStockValidation({
                input: { items: stockValidationItems }
            })

            if (!getValidationStock || getValidationStock.length === 0) {
                openToast(
                    t('No se pudo validar el stock'),
                    ToastType.Warning,
                    true
                )
                return
            }

            const addToCartItems = []
            let failed = 0

            for (const p of selectedProducts) {
                const requestedQty = qtyMap[p.sku] ?? 1
                const stockItem = getValidationStock.find(
                    (s) => s.sku === p.sku
                )

                const stock = Number(stockItem?.stock ?? 0)
                const finalQty = stock > 0 ? Math.min(requestedQty, stock) : 0

                if (finalQty === 0) {
                    failed++
                    continue
                }

                addToCartItems.push({
                    sku: p.sku,
                    quantity: finalQty,
                    selected_options: []
                })
            }

            if (addToCartItems.length === 0) {
                openToast(
                    t('No hay productos con stock disponible'),
                    ToastType.Warning,
                    true
                )
                return
            }

            const response = await addToCart(addToCartItems)

            const errorMessage =
                response?.errors?.[0]?.message ??
                response?.user_errors?.[0]?.message ??
                null

            if (!isMobile) {
                openSidebar()
                setTimeout(() => closeSidebar(), 3000)
            }

            if (response?.data && !errorMessage) {
                const addedCount = addToCartItems.length

                if (failed === 0) {
                    openCartToast(
                        t('Se agregaron {{n}} productos al carro', {
                            n: addedCount
                        }),
                        ToastType.Success
                    )
                } else {
                    openToast(
                        t('Se agregaron {{ok}} y fallaron {{bad}}', {
                            ok: addedCount,
                            bad: failed
                        }),
                        ToastType.Warning,
                        true
                    )
                }
            } else {
                openToast(
                    errorMessage ?? t('Error al agregar productos'),
                    ToastType.Warning,
                    true
                )
            }
        } catch (e) {
            console.error('Bulk add to cart error', e)
            openToast(
                t('Ocurrió un error al agregar los productos'),
                ToastType.Warning,
                true
            )
        } finally {
            setIsBulkAdding(false)
        }
    }

    const handleQtyChangeForSku =
        (sku: string) => (updater: SetStateAction<number>) => {
            setQtyMap((prev) => {
                const prevVal = prev[sku] ?? 1
                const newVal =
                    typeof updater === 'function' ? updater(prevVal) : updater

                return {
                    ...prev,
                    [sku]: newVal
                }
            })
        }

    const Card = (p: Product) => (
        <div className={styles.productCardInner}>
            <label className={styles.checkboxRow} aria-label={p.name}>
                <input
                    type="checkbox"
                    checked={!!checkedMap[p.sku]}
                    id={p.name}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleChecked(p.sku)}
                />
            </label>

            <button
                type="button"
                className={styles.productButton}
                onClick={() => handleOpenSidebar(p)}
            >
                <div
                    className={styles.productImage}
                    style={{ backgroundImage: `url(${p.image?.url})` }}
                />
                {!isMobile && (
                    <div
                        className={styles.qtySelector}
                        onClick={(e) => e.stopPropagation()}
                        role="presentation"
                    >
                        <QuantitySelector
                            quantity={qtyMap[p.sku] ?? 1}
                            setQuantity={handleQtyChangeForSku(p.sku)}
                        />
                    </div>
                )}
                <div className={styles.productDetail}>
                    <span className={styles.productName}>{p.name}</span>
                    <span className={styles.productPrice}>
                        {canShowPrice(p) && (
                            <ProductPrice
                                className={styles.groupedProductPrice}
                                priceRange={p.price_range}
                            />
                        )}
                    </span>
                    {p.is_set === 1 &&
                        typeof p.ppum === 'number' &&
                        !isNaN(p.ppum) && (
                            <div className={styles.pricePerUnitCont}>
                                <span>
                                    {t('common||pdp||Price per unit')}{' '}
                                    {formatCurrency(p.ppum ?? '')}
                                </span>
                            </div>
                        )}
                </div>
            </button>
        </div>
    )

    return (
        <div className={styles.groupedProductWrapper}>
            {/* HEADER: Set completo + precio + descripción + wishlist */}
            <div className={styles.groupedHeader}>
                <div className={styles.groupedHeaderRow}>
                    <p className={styles.groupedSubtitle}>
                        {t('pdp||Complete Set')}
                    </p>
                    <p className={styles.groupedTotalPrice}>
                        {formatCurrency(
                            groupedSetFinalPrice ?? dynamicGroupPrice ?? 0
                        )}
                    </p>
                </div>

                {descriptionHtml && (
                    <div className={styles.groupedDescription}>
                        <RichContent html={descriptionHtml} />
                    </div>
                )}

                {wishlist && (
                    <div className={styles.groupedWishlist}>{wishlist}</div>
                )}
            </div>

            {/* Barra principal: botón agregar al carro + texto dinámico */}
            <div className={styles.bulkAddBar}>
                <Button
                    type="button"
                    className={styles.bulkAddButton}
                    onClick={addSelectedToCart}
                    disabled={
                        checkedCount === 0 ||
                        dynamicGroupQty === 0 ||
                        isBulkAdding
                    }
                    loading={isBulkAdding}
                >
                    {/* MOBILE: texto + precio dentro del botón */}
                    {isMobile ? (
                        <span className={styles.mobileButtonLabel}>
                            {isBulkAdding ? t('Adding...') : t('Add to cart')}
                            {dynamicGroupPrice > 0 && (
                                <span className={styles.mobileButtonPrice}>
                                    {' '}
                                    ({formatCurrency(dynamicGroupPrice)})
                                </span>
                            )}
                        </span>
                    ) : (
                        /* DESKTOP: igual que antes, solo texto */
                        <>{isBulkAdding ? t('Adding...') : t('Add to cart')}</>
                    )}
                </Button>

                {/* DESKTOP: texto + precio fuera del botón, igual que antes */}
                {!isMobile && (
                    <div className={styles.bulkAddInfo}>
                        {t('Agregar {{ qty }} al carro', {
                            qty: dynamicGroupQty
                        })}
                        {dynamicGroupPrice > 0 && (
                            <span className={styles.bulkAddPrice}>
                                {' '}
                                <strong>
                                    ({formatCurrency(dynamicGroupPrice)})
                                </strong>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Carrusel de productos */}
            <div className={styles.setContainer}>
                <span className={styles.title}>
                    {t('pdp||Choose your products ({{ amount }} articles)', {
                        amount: dynamicGroupQty
                    })}
                </span>
                {shouldUseSlider && (
                    <div className={styles.arrows}>
                        <div
                            className={`${styles.leftArrowSlickSuggSet} ${
                                !isFirstSlide ? styles.active : ''
                            }`}
                            onClick={handlePrev}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                (e.key === 'Enter' || e.key === ' ') &&
                                handlePrev()
                            }
                        >
                            <ChevronFatRight />
                        </div>
                        <div
                            className={`${styles.rightArrowSlickSuggSet} ${
                                !isLastSlide ? styles.active : ''
                            }`}
                            onClick={handleNext}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                (e.key === 'Enter' || e.key === ' ') &&
                                handleNext()
                            }
                        >
                            <ChevronFatRight />
                        </div>
                    </div>
                )}
            </div>

            <Slider ref={sliderRef} {...settings}>
                {safeProducts.map((p) => (
                    <div
                        className={styles.productCardSlider}
                        key={`${p.id}-${p.sku}`}
                        data-sku={p.sku}
                    >
                        {Card(p)}
                    </div>
                ))}
            </Slider>

            {/* Sidebar de detalle de cada producto */}
            {isSidebarOpen && selectedProduct && (
                <SidebarGroupedProduct
                    open={isSidebarOpen}
                    onClose={handleCloseSidebar}
                    containScrollInChildren
                >
                    <div className={styles.sidebarPanel}>
                        <div className={styles.sidebarImageSlider}>
                            <Button
                                onClick={handleCloseSidebar}
                                variant="link"
                                className={cn(
                                    styles['cart-btn-close'],
                                    'close-btn'
                                )}
                                aria-label={t('Close Panel')}
                            >
                                <span aria-hidden="true">
                                    <Cross width={24} height={24} />
                                </span>
                            </Button>
                            <Slider
                                dots
                                infinite={false}
                                speed={300}
                                slidesToShow={1}
                                slidesToScroll={1}
                                arrows
                            >
                                {sidebarMedia.length > 0 ? (
                                    sidebarMedia.map((image, index) => (
                                        <div
                                            key={index}
                                            className={
                                                styles.sliderImageWrapper
                                            }
                                        >
                                            <img
                                                src={
                                                    image?.url ??
                                                    '/placeholder.svg'
                                                }
                                                alt={selectedProduct.name}
                                                className={styles.sliderImage}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className={styles.sliderImageWrapper}>
                                        <img
                                            src={
                                                selectedProduct.image?.url ??
                                                '/placeholder.svg'
                                            }
                                            alt={selectedProduct.name}
                                            className={styles.sliderImage}
                                        />
                                    </div>
                                )}
                            </Slider>
                        </div>

                        <div className={styles.infoContainer}>
                            <div className={styles.scrollableContent}>
                                <div className={styles.productDetails}>
                                    <div className={styles.productTopInfo}>
                                        <span
                                            className={styles.sku}
                                            aria-label={selectedProduct.sku}
                                        >
                                            {t('SKU: ')}
                                            {selectedProduct.sku}
                                        </span>
                                    </div>
                                    <div>
                                        <h1
                                            className={cn(
                                                styles['product-name'],
                                                'heading-4'
                                            )}
                                        >
                                            {selectedProduct.name}
                                        </h1>
                                    </div>

                                    <div className={styles.pdpPrice}>
                                        {canShowPrice(selectedProduct) && (
                                            <ProductPrice
                                                className={
                                                    styles.pdpProductPrice
                                                }
                                                priceRange={
                                                    selectedProduct.price_range
                                                }
                                            />
                                        )}
                                    </div>
                                    <div
                                        className={
                                            styles.shortDescriptionContainer
                                        }
                                    >
                                        <h5 className="uppercase font-bold text-base md:text-l leading-5 md:leading-6 mb-2">
                                            {t('pdp||Product description')}
                                        </h5>
                                        <div
                                            className={
                                                styles['shortDescription']
                                            }
                                        >
                                            {selectedProduct.short_description
                                                ?.html ? (
                                                <RichContent
                                                    html={
                                                        selectedProduct
                                                            .short_description
                                                            ?.html
                                                    }
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.buttonsContainer}>
                                <div className={styles.qtySelector}>
                                    <p className={styles.qtyTitle}>
                                        {t('pdp||QTY:')}
                                    </p>
                                    <QuantitySelector
                                        quantity={quantity}
                                        setQuantity={setQuantity}
                                    />
                                </div>
                                <Button
                                    aria-label={t('Add to Cart')}
                                    type="button"
                                    className={styles['add-to-cart-button']}
                                    onClick={addProductHandler}
                                    loading={false}
                                    disabled={isButtonDisabled}
                                >
                                    {buttonLabel}
                                </Button>
                            </div>
                        </div>
                    </div>
                </SidebarGroupedProduct>
            )}
        </div>
    )
}

export default GroupedProduct
